import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import { getAndClearOnboardingRedirectPath, getPostOnboardingPath, needsPersonaOnboarding } from '../../utils/onboardingUtils';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth/AuthProvider';
import LanguageQuickSwitcher from '../../components/v3/LanguageQuickSwitcher';

// 定义Persona类型
interface Persona {
  identities: string[];
  habits: string[];
  activities: string[];
  challenges: string[];
  goals: string[];
  preferences: {
    planningStyle: string[];
    socialStyle: string[];
    focusStyle: string[];
  };
  aiPreferences: string[];
}

// 定义各个步骤的选项
const identityOptions = [
  '小学生', '初中生', '高中生', '大学生', '研究生', '博士生',
  '职场新人', '资深职场人', '管理者', '创业者', '自由职业者',
  '艺术工作者', '技术人员', '医护人员', '教育工作者', '家庭主妇(夫)',
  '全职父母', '退休人士', '兼职工作者', '学生党', '打工人', '上班族',
  '科研人员', '设计师', '工程师', '销售', '市场营销', '客服', '人力资源'
];

const habitOptions = [
  '早起鸟', '夜猫子', '计划控', '随性派', '室内派', '户外控',
  '社交达人', '独处爱好者', '数字达人', '模拟爱好者', '咖啡成瘾',
  '茶文化爱好者', '健康饮食', '美食探索者', '极简主义者', '收藏控',
  '效率爱好者', '慢生活', '冥想习惯', '运动达人', '阅读爱好者'
];

const activityOptions = [
  '工作', '学习', '阅读', '写作', '编程', '设计', '绘画', '音乐', '电影',
  '游戏', '烹饪', '健身', '瑜伽', '跑步', '旅行', '摄影', '园艺', '手工',
  '冥想', '社交活动', '志愿服务', '投资理财', '收藏', '宠物陪伴', '看剧',
  '听播客', '逛街', '徒步', '骑行', '游泳', '爬山', '露营', '钓鱼'
];

const challengeOptions = [
  '时间不够用', '经常拖延', '难以专注', '容易分心', '健康问题',
  '睡眠不足', '压力大', '决策困难', '信息过载', '工作倦怠',
  '学习困难', '社交压力', '经济压力', '家庭责任', '工作与生活平衡',
  '缺乏动力', '目标不明确', '缺乏反馈', '环境干扰', '技能不足'
];

const goalOptions = [
  '提高工作效率', '学业进步', '职业发展', '创业成功', '学习新技能',
  '保持健康', '改善睡眠', '增进人际关系', '平衡生活', '财务自由',
  '减轻压力', '培养爱好', '完成项目', '旅行探索', '个人成长',
  '减肥健身', '学习语言', '阅读更多', '写作提升', '冥想习惯'
];

const planningStyleOptions = [
  '严格计划', '灵活安排', '即兴决定', '目标驱动', '流程导向',
  '短期规划', '长期规划', '分解任务', '整体思考', '优先级管理'
];

const socialStyleOptions = [
  '社交活跃', '独处为主', '小圈子', '线上社交', '职场社交',
  '深度交流', '广泛人脉', '社区参与', '家庭聚会', '兴趣小组'
];

const focusStyleOptions = [
  '深度专注', '多任务处理', '番茄工作法', '固定时段', '环境敏感',
  '音乐辅助', '早晨效率高', '夜间效率高', '需要截止压力', '自我激励'
];

const aiPreferenceOptions = [
  '日程规划', '任务提醒', '学习辅助', '工作助手', '生活建议',
  '健康提醒', '习惯养成', '创意激发', '情绪支持', '决策辅助',
  '信息筛选', '知识管理', '时间追踪', '目标监督', '效率优化',
  '智能推荐', '个性化建议', '数据分析', '自动化流程', '语音交互'
];

// 颜色配置
const sectionColors = {
  welcome: { from: 'from-blue-400', to: 'to-purple-500' },
  identity: { from: 'from-purple-400', to: 'to-pink-500' },
  habits: { from: 'from-pink-400', to: 'to-red-500' },
  activities: { from: 'from-red-400', to: 'to-orange-500' },
  challenges: { from: 'from-orange-400', to: 'to-yellow-500' },
  goals: { from: 'from-yellow-400', to: 'to-green-500' },
  preferences: { from: 'from-green-400', to: 'to-teal-500' },
  aiPreferences: { from: 'from-teal-400', to: 'to-blue-500' },
  summary: { from: 'from-blue-400', to: 'to-indigo-500' }
};

export const PersonaOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['v3/common', 'v3/me', 'v3/personaOnboarding']);
  const { user: authUser, signOut } = useAuth();

  // 判断是否为首次 onboarding（需要强制完成）还是编辑模式
  const [isFirstTimeOnboarding, setIsFirstTimeOnboarding] = useState<boolean>(true);
  const [onboardingCheckLoading, setOnboardingCheckLoading] = useState<boolean>(true);
  const isEditMode = !isFirstTimeOnboarding;

  // 异步检查 onboarding 状态
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (authUser) {
        try {
          const needsOnboarding = await needsPersonaOnboarding(authUser);
          setIsFirstTimeOnboarding(needsOnboarding);
        } catch (err) {
          console.error('[PersonaOnboarding] Error checking onboarding status:', err);
          // 出错时默认为首次 onboarding
          setIsFirstTimeOnboarding(true);
        } finally {
          setOnboardingCheckLoading(false);
        }
      } else {
        setOnboardingCheckLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [authUser]);

  // 引用各个区块
  const welcomeRef = useRef<HTMLDivElement>(null);
  const identityRef = useRef<HTMLDivElement>(null);
  const habitsRef = useRef<HTMLDivElement>(null);
  const activitiesRef = useRef<HTMLDivElement>(null);
  const challengesRef = useRef<HTMLDivElement>(null);
  const goalsRef = useRef<HTMLDivElement>(null);
  const preferencesRef = useRef<HTMLDivElement>(null);
  const aiPreferencesRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);

  // 当前活跃区块
  const [activeSection, setActiveSection] = useState('welcome');

  // Persona数据
  const [persona, setPersona] = useState<Persona>({
    identities: [],
    habits: [],
    activities: [],
    challenges: [],
    goals: [],
    preferences: {
      planningStyle: [],
      socialStyle: [],
      focusStyle: []
    },
    aiPreferences: []
  });

  // 加载已有的 persona 数据（编辑模式）
  useEffect(() => {
    const loadExistingPersona = async () => {
      if (isEditMode && authUser) {
        try {
          // 从 profiles 表获取 persona 数据
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('persona')
            .eq('id', authUser.id)
            .single();

          if (error) {
            console.error('[PersonaOnboarding] Error loading profile:', error);
            return;
          }

          if (profile?.persona) {
            const existingPersona = typeof profile.persona === 'string'
              ? JSON.parse(profile.persona)
              : profile.persona;

            console.log('[PersonaOnboarding] Loading existing persona data:', existingPersona);

            // 确保数据结构完整
            setPersona({
              identities: existingPersona.identities || [],
              habits: existingPersona.habits || [],
              activities: existingPersona.activities || [],
              challenges: existingPersona.challenges || [],
              goals: existingPersona.goals || [],
              preferences: {
                planningStyle: existingPersona.preferences?.planningStyle || [],
                socialStyle: existingPersona.preferences?.socialStyle || [],
                focusStyle: existingPersona.preferences?.focusStyle || []
              },
              aiPreferences: existingPersona.aiPreferences || []
            });
          }
        } catch (err) {
          console.error('[PersonaOnboarding] Error parsing persona data:', err);
        }
      }
    };

    loadExistingPersona();
  }, [isEditMode, authUser]);

  // 监听滚动，更新当前活跃区块
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      const sections = [
        { id: 'welcome', ref: welcomeRef },
        { id: 'identity', ref: identityRef },
        { id: 'habits', ref: habitsRef },
        { id: 'activities', ref: activitiesRef },
        { id: 'challenges', ref: challengesRef },
        { id: 'goals', ref: goalsRef },
        { id: 'preferences', ref: preferencesRef },
        { id: 'aiPreferences', ref: aiPreferencesRef },
        { id: 'summary', ref: summaryRef }
      ];

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.ref.current && section.ref.current.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 处理选择变化
  const handleMultiSelect = (category: keyof Persona, item: string) => {
    if (category === 'preferences') return;

    setPersona(prev => {
      const currentItems = prev[category] as string[];
      if (currentItems.includes(item)) {
        return {
          ...prev,
          [category]: currentItems.filter(i => i !== item)
        };
      } else {
        return {
          ...prev,
          [category]: [...currentItems, item]
        };
      }
    });
  };

  // 处理偏好选择
  const handlePreferenceSelect = (
    preference: 'planningStyle' | 'socialStyle' | 'focusStyle',
    item: string
  ) => {
    setPersona(prev => {
      const currentItems = prev.preferences[preference];
      if (currentItems.includes(item)) {
        return {
          ...prev,
          preferences: {
            ...prev.preferences,
            [preference]: currentItems.filter(i => i !== item)
          }
        };
      } else {
        return {
          ...prev,
          preferences: {
            ...prev.preferences,
            [preference]: [...currentItems, item]
          }
        };
      }
    });
  };

  // 滚动到下一个区块，使用页面切换效果
  const scrollToSection = (sectionRef: React.RefObject<HTMLDivElement>) => {
    if (sectionRef.current) {
      // 添加页面切换动画
      document.body.style.overflow = 'hidden'; // 防止滚动

      // 创建一个覆盖层用于过渡动画
      const overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.backgroundColor = 'rgba(255, 255, 255, 0)';
      overlay.style.zIndex = '9999';
      overlay.style.transition = 'background-color 0.3s ease';
      document.body.appendChild(overlay);

      // 触发重绘以应用初始样式
      overlay.getBoundingClientRect();

      // 淡入覆盖层
      overlay.style.backgroundColor = 'rgba(255, 255, 255, 0.5)';

      // 300ms后执行滚动和淡出
      setTimeout(() => {
        // 执行滚动
        sectionRef.current?.scrollIntoView({ behavior: 'auto' });

        // 淡出覆盖层
        overlay.style.backgroundColor = 'rgba(255, 255, 255, 0)';

        // 动画完成后移除覆盖层并恢复滚动
        setTimeout(() => {
          document.body.removeChild(overlay);
          document.body.style.overflow = '';
        }, 300);
      }, 300);
    }
  };

  // 处理返回按钮点击
  const handleBackClick = async () => {
    if (isFirstTimeOnboarding) {
      // 首次 onboarding，显示退出登录
      await signOut();
    } else {
      // 编辑模式，返回 Me 页面
      navigate('/v3/me');
    }
  };

  // 处理完成
  const handleComplete = async () => {
    try {
      // 将persona数据保存到 profiles 表
      if (authUser) {
        const updateData: any = {
          persona: JSON.stringify(persona),
          updated_at: new Date().toISOString()
        };

        // 如果是首次 onboarding，标记已完成
        if (isFirstTimeOnboarding) {
          updateData.onboarding_completed = true;
          updateData.onboarding_completed_at = new Date().toISOString();
        }

        // 更新 profiles 表
        const { error } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', authUser.id);

        if (error) {
          console.error('保存Persona数据失败:', error);
          return;
        }

        console.log('[PersonaOnboarding] Persona data saved successfully');
      }

      if (isFirstTimeOnboarding) {
        // 首次 onboarding，获取保存的重定向路径
        const redirectPath = getAndClearOnboardingRedirectPath();
        const targetPath = getPostOnboardingPath(redirectPath);

        console.log('[PersonaOnboarding] Onboarding completed, redirecting to:', targetPath);
        navigate(targetPath, { replace: true });
      } else {
        // 编辑模式，返回 Me 页面
        console.log('[PersonaOnboarding] Persona updated, returning to Me page');
        navigate('/v3/me');
      }
    } catch (error) {
      console.error('保存Persona数据失败:', error);
    }
  };

  // 处理跳过
  const handleSkip = async () => {
    try {
      if (authUser && isFirstTimeOnboarding) {
        // 创建标准的"跳过"persona结构
        const skippedPersona = {
          identities: [],
          habits: [],
          activities: [],
          challenges: [],
          goals: [],
          preferences: {
            planningStyle: [],
            socialStyle: [],
            focusStyle: []
          },
          aiPreferences: [],
          skipped: true,
          skippedAt: new Date().toISOString()
        };

        const updateData = {
          onboarding_completed: true,
          onboarding_completed_at: new Date().toISOString(),
          persona: JSON.stringify(skippedPersona),
          updated_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', authUser.id);

        if (error) {
          console.error('跳过 onboarding 失败:', error);
          return;
        }

        console.log('[PersonaOnboarding] Onboarding skipped successfully');

        // 跳转到主页
        const redirectPath = getAndClearOnboardingRedirectPath();
        const targetPath = getPostOnboardingPath(redirectPath);
        navigate(targetPath, { replace: true });
      }
    } catch (error) {
      console.error('跳过 onboarding 失败:', error);
    }
  };

  // 标签云的颜色映射
  const tagColorMap: Record<string, { bg: string, text: string, icon?: string }> = {
    // 身份类别
    '小学生': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', icon: '🧒' },
    '初中生': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300', icon: '📚' },
    '高中生': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '🎒' },
    '大学生': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', icon: '🎓' },
    '研究生': { bg: 'bg-lime-100 dark:bg-lime-900/30', text: 'text-lime-800 dark:text-lime-300', icon: '🔬' },
    '博士生': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: '📝' },
    '职场新人': { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-800 dark:text-emerald-300', icon: '💼' },
    '资深职场人': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', icon: '👔' },
    '管理者': { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-300', icon: '👨‍💼' },
    '创业者': { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-800 dark:text-sky-300', icon: '🚀' },
    '自由职业者': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '🏝️' },
    '艺术工作者': { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300', icon: '🎨' },
    '技术人员': { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-800 dark:text-violet-300', icon: '💻' },
    '医护人员': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', icon: '⚕️' },
    '教育工作者': { bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/30', text: 'text-fuchsia-800 dark:text-fuchsia-300', icon: '📖' },
    '家庭主妇(夫)': { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-800 dark:text-pink-300', icon: '🏠' },
    '全职父母': { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-800 dark:text-rose-300', icon: '👨‍👩‍👧' },
    '退休人士': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300', icon: '🌴' },
    '兼职工作者': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '⏱️' },
    '学生党': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', icon: '📝' },
    '打工人': { bg: 'bg-lime-100 dark:bg-lime-900/30', text: 'text-lime-800 dark:text-lime-300', icon: '💪' },
    '上班族': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: '🏢' },
    '科研人员': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', icon: '🔭' },
    '设计师': { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-300', icon: '✏️' },
    '工程师': { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-800 dark:text-sky-300', icon: '🔧' },
    '销售': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '📊' },
    '市场营销': { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300', icon: '📈' },
    '客服': { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-800 dark:text-violet-300', icon: '🎧' },
    '人力资源': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', icon: '👥' },

    // 习惯类别
    '早起鸟': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '🌅' },
    '夜猫子': { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300', icon: '🌙' },
    '计划控': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '📅' },
    '随性派': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', icon: '🦋' },
    '室内派': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', icon: '🏠' },
    '户外控': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: '🏞️' },
    '社交达人': { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-800 dark:text-pink-300', icon: '👯' },
    '独处爱好者': { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-800 dark:text-violet-300', icon: '🧘' },
    '数字达人': { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-800 dark:text-sky-300', icon: '💻' },
    '模拟爱好者': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '📚' },
    '咖啡成瘾': { bg: 'bg-brown-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '☕' },
    '茶文化爱好者': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: '🍵' },
    '健康饮食': { bg: 'bg-lime-100 dark:bg-lime-900/30', text: 'text-lime-800 dark:text-lime-300', icon: '🥗' },
    '美食探索者': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', icon: '🍜' },
    '极简主义者': { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-800 dark:text-gray-300', icon: '⚪' },
    '收藏控': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', icon: '🏆' },
    '效率爱好者': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '⚡' },
    '慢生活': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', icon: '🐢' },
    '冥想习惯': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', icon: '🧘‍♀️' },
    '运动达人': { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-800 dark:text-emerald-300', icon: '🏃' },
    '阅读爱好者': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '📚' },

    // 计划风格类别
    '严格计划': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '📅' },
    '灵活安排': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', icon: '🔄' },
    '即兴决定': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', icon: '💭' },
    '目标驱动': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: '🎯' },
    '流程导向': { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300', icon: '📋' },
    '短期规划': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', icon: '📌' },
    '长期规划': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '🗓️' },
    '分解任务': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '📊' },
    '整体思考': { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-800 dark:text-violet-300', icon: '🧩' },
    '优先级管理': { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-300', icon: '⚖️' },

    // 社交风格类别
    '社交活跃': { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-800 dark:text-pink-300', icon: '🎭' },
    '独处为主': { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300', icon: '🧘' },
    '小圈子': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', icon: '👪' },
    '线上社交': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '💻' },
    '职场社交': { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-800 dark:text-gray-300', icon: '👔' },
    '深度交流': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', icon: '🗣️' },
    '广泛人脉': { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-300', icon: '🌐' },
    '社区参与': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: '🏘️' },
    '家庭聚会': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '🏠' },
    '兴趣小组': { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-800 dark:text-rose-300', icon: '🎨' },

    // 专注风格类别
    '深度专注': { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300', icon: '🔍' },
    '多任务处理': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '🔀' },
    '番茄工作法': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', icon: '🍅' },
    '固定时段': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300', icon: '⏱️' },
    '环境敏感': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: '🏞️' },
    '音乐辅助': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', icon: '🎵' },
    '早晨效率高': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', icon: '🌅' },
    '夜间效率高': { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300', icon: '🌙' },
    '需要截止压力': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300', icon: '⏳' },
    '自我激励': { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-800 dark:text-rose-300', icon: '🔥' },
  };

  // 获取标签颜色
  const getTagColor = (option: string, category: string): { bg: string, text: string, icon?: string } => {
    if (tagColorMap[option]) {
      return tagColorMap[option];
    }

    // 默认颜色映射
    const defaultColors: Record<string, { bg: string, text: string, icon?: string }> = {
      'identities': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300', icon: '👤' },
      'habits': { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-800 dark:text-pink-300', icon: '🔄' },
      'activities': { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300', icon: '🏃' },
      'challenges': { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', icon: '⚠️' },
      'goals': { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: '🎯' },
      'planningStyle': { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300', icon: '📋' },
      'socialStyle': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300', icon: '👥' },
      'focusStyle': { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300', icon: '🧠' },
      'aiPreferences': { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-300', icon: '🤖' },
    };

    return defaultColors[category] || { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-800 dark:text-gray-300' };
  };

  // 渲染标签云
  const renderTagCloud = (
    options: string[],
    selectedItems: string[],
    onSelect: (item: string) => void,
    colorClass: { from: string, to: string },
    category: string = 'identities'
  ) => {
    // 获取字体粗细 - 基于选项的重要性
    const getFontWeight = (option: string): string => {
      // 定义选项重要性级别
      const optionImportance = getOptionImportance(option);

      // 4种不同的粗细
      const weights = [
        'font-normal', // 正常
        'font-medium', // 中等
        'font-semibold', // 粗体
        'font-bold'    // 最粗
      ];

      // 根据重要性级别选择粗细
      if (optionImportance === 'highest') {
        return weights[3]; // 最高重要性使用最粗字体
      } else if (optionImportance === 'high') {
        return weights[2]; // 高重要性使用粗体
      } else if (optionImportance === 'medium') {
        return weights[1]; // 中等重要性使用中等粗细
      } else {
        return weights[0]; // 低重要性使用正常粗细
      }
    };

    // 确定选项的重要性级别
    const getOptionImportance = (option: string): 'highest' | 'high' | 'medium' | 'low' => {
      // 最高重要性 - 主要客户群体
      const highestImportanceOptions = [
        '大学生', '职场新人', '上班族', '技术人员', '设计师', '工程师'
      ];

      // 高重要性 - 次要客户群体
      const highImportanceOptions = [
        '研究生', '博士生', '资深职场人', '管理者', '创业者', '科研人员',
        '早起鸟', '夜猫子', '工作', '学习', '写作', '编程'
      ];

      // 中等重要性
      const mediumImportanceOptions = [
        '高中生', '自由职业者', '艺术工作者', '医护人员', '教育工作者',
        '学生党', '上班族', '阅读', '健身', '音乐', '电影'
      ];

      // 判断选项的重要性级别
      if (highestImportanceOptions.includes(option)) {
        return 'highest';
      } else if (highImportanceOptions.includes(option)) {
        return 'high';
      } else if (mediumImportanceOptions.includes(option)) {
        return 'medium';
      } else {
        return 'low';
      }
    };

    // 确定每个标签的大小类别 - 基于选项的重要性
    const getSizeClass = (option: string) => {
      // 获取选项的重要性级别
      const importance = getOptionImportance(option);

      // 5种不同的大小类别
      const sizes = [
        'text-xs py-1 px-2', // 最小
        'text-sm py-1.5 px-3', // 小
        'text-base py-2 px-4', // 中
        'text-lg py-2.5 px-5', // 大
        'text-xl py-3 px-6' // 最大
      ];

      // 根据重要性级别选择大小
      if (importance === 'highest') {
        return sizes[4]; // 最高重要性使用最大尺寸
      } else if (importance === 'high') {
        return sizes[3]; // 高重要性使用大尺寸
      } else if (importance === 'medium') {
        return sizes[2]; // 中等重要性使用中等尺寸
      } else {
        return sizes[1]; // 低重要性使用小尺寸
      }
    };

    return (
      <div className="flex flex-wrap gap-2 mt-4 justify-center">
        {options.map((option, index) => {
          const sizeClass = getSizeClass(option);
          const tagColor = getTagColor(option, category);
          const icon = tagColor.icon || '';

          return (
            <motion.button
              key={option}
              onClick={() => onSelect(option)}
              className={`rounded-full transition-all ${sizeClass} ${
                selectedItems.includes(option)
                  ? `bg-gradient-to-r ${colorClass.from} ${colorClass.to} text-white shadow-md font-semibold`
                  : `${tagColor.bg} ${tagColor.text} border border-gray-200 dark:border-gray-700 ${getFontWeight(option)}`
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: { delay: index * 0.03, duration: 0.5 }
              }}
            >
              {icon && <span className="mr-1">{icon}</span>}
              {option}
            </motion.button>
          );
        })}
      </div>
    );
  };

  // 活动的图标和颜色映射
  const activityIconMap: Record<string, string> = {
    '工作': '💼', '学习': '📚', '阅读': '📖', '写作': '✍️', '编程': '💻',
    '设计': '🎨', '绘画': '🖌️', '音乐': '🎵', '电影': '🎬', '游戏': '🎮',
    '烹饪': '🍳', '健身': '💪', '瑜伽': '🧘‍♀️', '跑步': '🏃', '旅行': '✈️',
    '摄影': '📷', '园艺': '🌱', '手工': '🧶', '冥想': '🧘', '社交活动': '👥',
    '志愿服务': '🤝', '投资理财': '💰', '收藏': '🏆', '宠物陪伴': '🐾', '看剧': '📺',
    '听播客': '🎧', '逛街': '🛍️', '徒步': '🥾', '骑行': '🚴', '游泳': '🏊',
    '爬山': '⛰️', '露营': '⛺', '钓鱼': '🎣'
  };

  // 为活动添加到标签颜色映射
  Object.entries(activityIconMap).forEach(([activity, icon]) => {
    if (!tagColorMap[activity]) {
      // 为每个活动生成一个随机但固定的颜色
      const colors = [
        { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300' },
        { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300' },
        { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300' },
        { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300' },
        { bg: 'bg-lime-100 dark:bg-lime-900/30', text: 'text-lime-800 dark:text-lime-300' },
        { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300' },
        { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-800 dark:text-emerald-300' },
        { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300' },
        { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-300' },
        { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-800 dark:text-sky-300' },
        { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300' },
        { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300' },
        { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-800 dark:text-violet-300' },
        { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-800 dark:text-purple-300' },
        { bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/30', text: 'text-fuchsia-800 dark:text-fuchsia-300' },
        { bg: 'bg-pink-100 dark:bg-pink-900/30', text: 'text-pink-800 dark:text-pink-300' },
        { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-800 dark:text-rose-300' }
      ];

      // 使用活动名称的哈希值来确定颜色索引
      const hash = activity.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const color = colors[hash % colors.length];

      // 添加到标签颜色映射
      tagColorMap[activity] = { ...color, icon };
    }
  });

  const aiPreferenceIconMap: Record<string, string> = {
    '日程规划': '📅', '任务提醒': '⏰', '学习辅助': '📚', '工作助手': '💼', '生活建议': '🏠',
    '健康提醒': '❤️', '习惯养成': '🔄', '创意激发': '💡', '情绪支持': '😊', '决策辅助': '🤔',
    '信息筛选': '🔍', '知识管理': '🧠', '时间追踪': '⏱️', '目标监督': '🎯', '效率优化': '⚡',
    '智能推荐': '👍', '个性化建议': '🌟', '数据分析': '📊', '自动化流程': '🤖', '语音交互': '🎤'
  };

  // 为AI偏好添加到标签颜色映射
  Object.entries(aiPreferenceIconMap).forEach(([preference, icon]) => {
    if (!tagColorMap[preference]) {
      // 为每个偏好生成一个随机但固定的颜色
      const colors = [
        { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-800 dark:text-cyan-300' },
        { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300' },
        { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-800 dark:text-blue-300' },
        { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-800 dark:text-indigo-300' },
        { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-800 dark:text-sky-300' },
        { bg: 'bg-violet-100 dark:bg-violet-900/30', text: 'text-violet-800 dark:text-violet-300' }
      ];

      // 使用偏好名称的哈希值来确定颜色索引
      const hash = preference.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const color = colors[hash % colors.length];

      // 添加到标签颜色映射
      tagColorMap[preference] = { ...color, icon };
    }
  });

  // 渲染图标选择器
  const renderIconSelector = (
    options: string[],
    selectedItems: string[],
    onSelect: (item: string) => void,
    colorClass: { from: string, to: string },
    category: string = 'activities'
  ) => {
    // 为每个选项获取图标
    const getIconForOption = (option: string): string => {
      if (category === 'activities' && activityIconMap[option]) {
        return activityIconMap[option];
      }
      if (category === 'aiPreferences' && aiPreferenceIconMap[option]) {
        return aiPreferenceIconMap[option];
      }
      if (category === 'planningStyle' && planningStyleIconMap[option]) {
        return planningStyleIconMap[option];
      }
      if (category === 'socialStyle' && socialStyleIconMap[option]) {
        return socialStyleIconMap[option];
      }
      if (category === 'focusStyle' && focusStyleIconMap[option]) {
        return focusStyleIconMap[option];
      }
      // 如果没有特定图标，使用标签颜色中的图标或首字母
      const tagColor = getTagColor(option, category);
      return tagColor.icon || option.charAt(0);
    };

    // 确定选项的重要性级别
    const getOptionImportance = (option: string): 'highest' | 'high' | 'medium' | 'low' => {
      // 最高重要性 - 主要客户群体
      const highestImportanceOptions = [
        '大学生', '职场新人', '上班族', '技术人员', '设计师', '工程师',
        '工作', '学习', '编程', '写作', '阅读'
      ];

      // 高重要性 - 次要客户群体
      const highImportanceOptions = [
        '研究生', '博士生', '资深职场人', '管理者', '创业者', '科研人员',
        '早起鸟', '夜猫子', '健身', '旅行', '音乐', '电影'
      ];

      // 中等重要性
      const mediumImportanceOptions = [
        '高中生', '自由职业者', '艺术工作者', '医护人员', '教育工作者',
        '学生党', '社交活动', '游戏', '烹饪', '摄影'
      ];

      // 判断选项的重要性级别
      if (highestImportanceOptions.includes(option)) {
        return 'highest';
      } else if (highImportanceOptions.includes(option)) {
        return 'high';
      } else if (mediumImportanceOptions.includes(option)) {
        return 'medium';
      } else {
        return 'low';
      }
    };

    // 获取图标大小 - 基于选项的重要性
    const getIconSize = (option: string): string => {
      // 获取选项的重要性级别
      const importance = getOptionImportance(option);

      // 4种不同的大小类别
      const sizes = [
        'w-10 h-10 text-xl', // 小
        'w-12 h-12 text-2xl', // 中
        'w-14 h-14 text-2xl', // 大
        'w-16 h-16 text-3xl'  // 最大
      ];

      // 根据重要性级别选择大小
      if (importance === 'highest') {
        return sizes[3]; // 最高重要性使用最大尺寸
      } else if (importance === 'high') {
        return sizes[2]; // 高重要性使用大尺寸
      } else if (importance === 'medium') {
        return sizes[1]; // 中等重要性使用中等尺寸
      } else {
        return sizes[0]; // 低重要性使用小尺寸
      }
    };

    // 获取背景颜色
    const getBgColor = (option: string, isSelected: boolean): string => {
      if (isSelected) {
        return `bg-gradient-to-r ${colorClass.from} ${colorClass.to}`;
      }

      // 为每个选项分配一个固定的颜色
      const colors = [
        'bg-red-50 dark:bg-red-900/20', 'bg-orange-50 dark:bg-orange-900/20',
        'bg-amber-50 dark:bg-amber-900/20', 'bg-yellow-50 dark:bg-yellow-900/20',
        'bg-lime-50 dark:bg-lime-900/20', 'bg-green-50 dark:bg-green-900/20',
        'bg-emerald-50 dark:bg-emerald-900/20', 'bg-teal-50 dark:bg-teal-900/20',
        'bg-cyan-50 dark:bg-cyan-900/20', 'bg-sky-50 dark:bg-sky-900/20',
        'bg-blue-50 dark:bg-blue-900/20', 'bg-indigo-50 dark:bg-indigo-900/20',
        'bg-violet-50 dark:bg-violet-900/20', 'bg-purple-50 dark:bg-purple-900/20',
        'bg-fuchsia-50 dark:bg-fuchsia-900/20', 'bg-pink-50 dark:bg-pink-900/20',
        'bg-rose-50 dark:bg-rose-900/20'
      ];

      // 使用选项字符串的哈希值来确定颜色索引
      const hash = option.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    };

    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 mt-6">
        {options.map((option, index) => {
          const isSelected = selectedItems.includes(option);
          const bgColor = getBgColor(option, isSelected);
          const icon = getIconForOption(option);

          return (
            <motion.button
              key={option}
              onClick={() => onSelect(option)}
              className={`flex flex-col items-center justify-center p-3 rounded-xl ${
                isSelected
                  ? `${bgColor} text-white shadow-md`
                  : `${bgColor} text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700`
              }`}
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: { delay: index * 0.03, duration: 0.5 }
              }}
            >
              <div className={`rounded-full flex items-center justify-center mb-2 ${
                isSelected
                  ? 'bg-white/20'
                  : 'bg-white dark:bg-gray-800 shadow-inner'
              } ${getIconSize(option)}`}>
                {icon}
              </div>
              <span className={`text-sm font-medium text-center ${
                isSelected ? 'text-white' : 'text-gray-700 dark:text-gray-200'
              }`}>
                {option}
              </span>
            </motion.button>
          );
        })}
      </div>
    );
  };

  // 目标和挑战的图标映射
  const goalIconMap: Record<string, string> = {
    '提高工作效率': '⚡', '学业进步': '📚', '职业发展': '📈', '创业成功': '🚀', '学习新技能': '🧠',
    '保持健康': '❤️', '改善睡眠': '😴', '增进人际关系': '👥', '平衡生活': '☯️', '财务自由': '💰',
    '减轻压力': '🧘', '培养爱好': '🎨', '完成项目': '✅', '旅行探索': '🌍', '个人成长': '🌱',
    '减肥健身': '💪', '学习语言': '🗣️', '阅读更多': '📖', '写作提升': '✍️', '冥想习惯': '🧘‍♀️'
  };

  // 为目标添加到标签颜色映射
  Object.entries(goalIconMap).forEach(([goal, icon]) => {
    if (!tagColorMap[goal]) {
      // 为每个目标生成一个随机但固定的颜色
      const colors = [
        { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300' },
        { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-800 dark:text-emerald-300' },
        { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-800 dark:text-teal-300' },
        { bg: 'bg-lime-100 dark:bg-lime-900/30', text: 'text-lime-800 dark:text-lime-300' },
        { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300' }
      ];

      // 使用目标名称的哈希值来确定颜色索引
      const hash = goal.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const color = colors[hash % colors.length];

      // 添加到标签颜色映射
      tagColorMap[goal] = { ...color, icon };
    }
  });

  const challengeIconMap: Record<string, string> = {
    '时间不够用': '⏰', '经常拖延': '⏳', '难以专注': '🧠', '容易分心': '🔄', '健康问题': '🏥',
    '睡眠不足': '😴', '压力大': '😓', '决策困难': '🤔', '信息过载': '📊', '工作倦怠': '😩',
    '学习困难': '📚', '社交压力': '👥', '经济压力': '💰', '家庭责任': '👨‍👩‍👧', '工作与生活平衡': '⚖️',
    '缺乏动力': '🔋', '目标不明确': '🎯', '缺乏反馈': '💬', '环境干扰': '🔊', '技能不足': '🛠️'
  };

  // 为挑战添加到标签颜色映射
  Object.entries(challengeIconMap).forEach(([challenge, icon]) => {
    if (!tagColorMap[challenge]) {
      // 为每个挑战生成一个随机但固定的颜色
      const colors = [
        { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300' },
        { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-800 dark:text-amber-300' },
        { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300' },
        { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300' },
        { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-800 dark:text-rose-300' }
      ];

      // 使用挑战名称的哈希值来确定颜色索引
      const hash = challenge.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const color = colors[hash % colors.length];

      // 添加到标签颜色映射
      tagColorMap[challenge] = { ...color, icon };
    }
  });

  // 计划风格图标映射
  const planningStyleIconMap: Record<string, string> = {
    '严格计划': '📅', '灵活安排': '🔄', '即兴决定': '💭', '目标驱动': '🎯',
    '流程导向': '📋', '短期规划': '📌', '长期规划': '🗓️', '分解任务': '📊',
    '整体思考': '🧩', '优先级管理': '⚖️'
  };

  // 社交风格图标映射
  const socialStyleIconMap: Record<string, string> = {
    '社交活跃': '🎭', '独处为主': '🧘', '小圈子': '👪', '线上社交': '💻',
    '职场社交': '👔', '深度交流': '🗣️', '广泛人脉': '🌐', '社区参与': '🏘️',
    '家庭聚会': '🏠', '兴趣小组': '🎨'
  };

  // 专注风格图标映射
  const focusStyleIconMap: Record<string, string> = {
    '深度专注': '🔍', '多任务处理': '🔀', '番茄工作法': '🍅', '固定时段': '⏱️',
    '环境敏感': '🏞️', '音乐辅助': '🎵', '早晨效率高': '🌅', '夜间效率高': '🌙',
    '需要截止压力': '⏳', '自我激励': '🔥'
  };

  // 渲染气泡选择器
  const renderBubbleSelector = (
    options: string[],
    selectedItems: string[],
    onSelect: (item: string) => void,
    colorClass: { from: string, to: string },
    category: string = 'goals'
  ) => {
    // 获取图标
    const getIconForOption = (option: string): string => {
      if (category === 'goals' && goalIconMap[option]) {
        return goalIconMap[option];
      }
      if (category === 'challenges' && challengeIconMap[option]) {
        return challengeIconMap[option];
      }
      if (category === 'planningStyle' && planningStyleIconMap[option]) {
        return planningStyleIconMap[option];
      }
      if (category === 'socialStyle' && socialStyleIconMap[option]) {
        return socialStyleIconMap[option];
      }
      if (category === 'focusStyle' && focusStyleIconMap[option]) {
        return focusStyleIconMap[option];
      }
      const tagColor = getTagColor(option, category);
      return tagColor.icon || '';
    };

    // 确定选项的重要性级别
    const getOptionImportance = (option: string): 'highest' | 'high' | 'medium' | 'low' => {
      // 最高重要性 - 主要目标和挑战
      const highestImportanceOptions = [
        '提高工作效率', '学习新技能', '保持健康', '平衡生活', '个人成长',
        '时间不够用', '经常拖延', '难以专注', '压力大', '工作与生活平衡'
      ];

      // 高重要性 - 次要目标和挑战
      const highImportanceOptions = [
        '职业发展', '学业进步', '改善睡眠', '减轻压力', '完成项目',
        '容易分心', '决策困难', '信息过载', '工作倦怠', '缺乏动力'
      ];

      // 中等重要性
      const mediumImportanceOptions = [
        '增进人际关系', '财务自由', '培养爱好', '旅行探索', '减肥健身',
        '学习困难', '社交压力', '经济压力', '家庭责任', '环境干扰'
      ];

      // 判断选项的重要性级别
      if (highestImportanceOptions.includes(option)) {
        return 'highest';
      } else if (highImportanceOptions.includes(option)) {
        return 'high';
      } else if (mediumImportanceOptions.includes(option)) {
        return 'medium';
      } else {
        return 'low';
      }
    };

    // 获取图标文本大小 - 基于选项的重要性
    const getIconTextSize = (option: string): string => {
      // 获取选项的重要性级别
      const importance = getOptionImportance(option);

      // 根据重要性选择大小
      if (importance === 'highest') {
        return 'text-2xl'; // 最高重要性
      } else if (importance === 'high') {
        return 'text-xl'; // 高重要性
      } else if (importance === 'medium') {
        return 'text-lg'; // 中等重要性
      } else {
        return 'text-base'; // 低重要性
      }
    };

    // 获取文本大小 - 基于选项的重要性
    const getTextSize = (option: string): string => {
      // 获取选项的重要性级别
      const importance = getOptionImportance(option);

      // 根据重要性选择大小和粗细
      if (importance === 'highest') {
        return 'text-sm font-bold'; // 最高重要性
      } else if (importance === 'high') {
        return 'text-sm font-semibold'; // 高重要性
      } else if (importance === 'medium') {
        return 'text-xs font-medium'; // 中等重要性
      } else {
        return 'text-xs'; // 低重要性
      }
    };

    // 获取气泡大小 - 基于选项的重要性和文本长度
    const getBubbleSize = (option: string): number => {
      const baseSize = 80; // 基础大小
      const lengthFactor = option.length * 1.5; // 文本长度因子（减小影响）

      // 获取选项的重要性级别
      const importance = getOptionImportance(option);

      // 根据重要性添加大小
      let importanceFactor = 0;
      if (importance === 'highest') {
        importanceFactor = 30; // 最高重要性
      } else if (importance === 'high') {
        importanceFactor = 20; // 高重要性
      } else if (importance === 'medium') {
        importanceFactor = 10; // 中等重要性
      }

      // 计算最终大小，确保在70-130px之间
      return Math.min(Math.max(baseSize + lengthFactor + importanceFactor, 70), 130);
    };

    // 获取气泡颜色
    const getBubbleColor = (option: string, isSelected: boolean): string => {
      if (isSelected) {
        return `bg-gradient-to-r ${colorClass.from} ${colorClass.to}`;
      }

      // 为每个选项分配一个固定的颜色
      const colors = [
        'bg-gradient-to-br from-red-100 to-pink-100 dark:from-red-900/30 dark:to-pink-900/30',
        'bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30',
        'bg-gradient-to-br from-yellow-100 to-lime-100 dark:from-yellow-900/30 dark:to-lime-900/30',
        'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30',
        'bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/30 dark:to-cyan-900/30',
        'bg-gradient-to-br from-sky-100 to-blue-100 dark:from-sky-900/30 dark:to-blue-900/30',
        'bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-900/30 dark:to-violet-900/30',
        'bg-gradient-to-br from-purple-100 to-fuchsia-100 dark:from-purple-900/30 dark:to-fuchsia-900/30',
        'bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-900/30 dark:to-rose-900/30'
      ];

      // 使用选项字符串的哈希值来确定颜色索引
      const hash = option.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    };

    return (
      <div className="flex flex-wrap gap-3 justify-center mt-6 relative">
        {options.map((option, index) => {
          const isSelected = selectedItems.includes(option);
          const size = getBubbleSize(option);
          const bgColor = getBubbleColor(option, isSelected);
          const icon = getIconForOption(option);

          return (
            <motion.button
              key={option}
              onClick={() => onSelect(option)}
              className={`rounded-full flex flex-col items-center justify-center p-2 ${
                isSelected
                  ? `${bgColor} text-white shadow-lg`
                  : `${bgColor} text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700`
              }`}
              style={{ width: `${size}px`, height: `${size}px` }}
              whileHover={{ scale: 1.1, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: {
                  delay: index * 0.05,
                  duration: 0.5,
                  type: "spring",
                  stiffness: 200
                }
              }}
            >
              {icon && <span className={`mb-1 ${getIconTextSize(option)}`}>{icon}</span>}
              <span className={`text-center leading-tight px-1 ${getTextSize(option)}`}>
                {option}
              </span>
            </motion.button>
          );
        })}
      </div>
    );
  };

  // 渲染进度指示器
  const renderProgressIndicator = () => {
    const sections = [
      'welcome', 'identity', 'habits', 'activities',
      'challenges', 'goals', 'preferences', 'aiPreferences', 'summary'
    ];

    const sectionRefs = {
      welcome: welcomeRef,
      identity: identityRef,
      habits: habitsRef,
      activities: activitiesRef,
      challenges: challengesRef,
      goals: goalsRef,
      preferences: preferencesRef,
      aiPreferences: aiPreferencesRef,
      summary: summaryRef
    };

    return (
      <div className="fixed top-16 right-4 z-50">
        <div className="flex flex-col space-y-2">
          {sections.map((section) => (
            <motion.button
              key={section}
              onClick={() => scrollToSection(sectionRefs[section as keyof typeof sectionRefs])}
              className={`w-3 h-3 rounded-full ${
                activeSection === section
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 shadow-md'
                  : 'bg-gray-300 dark:bg-gray-700'
              }`}
              whileHover={{ scale: 1.5 }}
              whileTap={{ scale: 0.9 }}
              animate={{
                scale: activeSection === section ? 1.2 : 1,
                transition: { duration: 0.3 }
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  // 页面切换效果
  useEffect(() => {
    // 监听键盘事件，支持上下键和空格键切换页面
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        navigateToNextSection();
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        navigateToPrevSection();
      }
    };

    // 添加事件监听器
    window.addEventListener('keydown', handleKeyDown);

    // 清理函数
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeSection]);

  // 定义部分引用映射
  const sectionRefsMap = {
    welcome: welcomeRef,
    identity: identityRef,
    habits: habitsRef,
    activities: activitiesRef,
    challenges: challengesRef,
    goals: goalsRef,
    preferences: preferencesRef,
    aiPreferences: aiPreferencesRef,
    summary: summaryRef
  };

  // 导航到下一个部分
  const navigateToNextSection = () => {
    const sections = [
      'welcome', 'identity', 'habits', 'activities',
      'challenges', 'goals', 'preferences', 'aiPreferences', 'summary'
    ];

    const currentIndex = sections.indexOf(activeSection);
    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1] as keyof typeof sectionRefsMap;
      scrollToSection(sectionRefsMap[nextSection]);
    }
  };

  // 导航到上一个部分
  const navigateToPrevSection = () => {
    const sections = [
      'welcome', 'identity', 'habits', 'activities',
      'challenges', 'goals', 'preferences', 'aiPreferences', 'summary'
    ];

    const currentIndex = sections.indexOf(activeSection);
    if (currentIndex > 0) {
      const prevSection = sections[currentIndex - 1] as keyof typeof sectionRefsMap;
      scrollToSection(sectionRefsMap[prevSection]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-ios-dark-gray6 font-ios pb-24 overflow-x-hidden">
      {/* 固定导航栏 */}
      <div className="sticky top-0 z-40 w-full backdrop-blur-md">
        <div className="bg-gradient-to-r from-blue-500/90 to-purple-500/90 text-white">
          <div className="container mx-auto px-4 py-3 flex justify-between items-center">
            <button
              onClick={handleBackClick}
              className="flex items-center text-white py-2"
            >
              <svg className="w-5 h-5 mr-1" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="text-ios-body font-medium">
                {isFirstTimeOnboarding ? '退出登录' : t('v3/personaOnboarding:navigation.back')}
              </span>
            </button>
            <h1 className="text-ios-headline font-semibold text-center">{t('v3/personaOnboarding:header.title')}</h1>
            <button
              onClick={isEditMode ? handleComplete : handleSkip}
              className="text-ios-body font-medium"
            >
              {isEditMode ? '保存' : '跳过'}
            </button>
          </div>
        </div>
      </div>

      {/* 进度指示器 */}
      {renderProgressIndicator()}

      {/* 导航按钮 */}
      <div className="fixed bottom-8 right-8 z-50 flex flex-col gap-4">
        <motion.button
          onClick={navigateToPrevSection}
          className="w-12 h-12 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-sm"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <svg className="w-6 h-6 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.button>
        <motion.button
          onClick={navigateToNextSection}
          className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center shadow-sm border border-white/30"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }}
        >
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </motion.button>
      </div>

      {/* 主内容区域 */}
      <div className="container mx-auto px-4 py-0">
        {/* 欢迎区块 */}
        <motion.section
          ref={welcomeRef}
          id="welcome"
          className="min-h-[90vh] flex flex-col justify-center items-center py-12 relative overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* 背景装饰元素 */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-400/10 dark:bg-blue-900/20 rounded-full -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-purple-400/10 dark:bg-purple-900/20 rounded-full -ml-28 -mb-28" />
          <div className="absolute top-1/4 left-1/6 w-20 h-20 bg-pink-400/10 dark:bg-pink-900/20 rounded-full" />
          <div className="absolute bottom-1/3 right-1/6 w-16 h-16 bg-indigo-400/10 dark:bg-indigo-900/20 rounded-full" />

          {/* 主要内容 */}
          <div className="relative z-10">
            <motion.div
              className={`w-56 h-56 mb-8 rounded-full bg-gradient-to-r ${sectionColors.welcome.from} ${sectionColors.welcome.to} flex items-center justify-center border-4 border-white/30 dark:border-gray-800/30`}
              animate={{
                scale: [1, 1.05, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: "reverse"
              }}
            >
              <div className="relative">
                <svg className="w-28 h-28 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>

                {/* 装饰性图标 */}
                <motion.div
                  className="absolute -top-4 -right-4 bg-yellow-400 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl border-2 border-white/30"
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 10, 0]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: 1
                  }}
                >
                  🎯
                </motion.div>
                <motion.div
                  className="absolute -bottom-2 -left-4 bg-green-400 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl border-2 border-white/30"
                  animate={{
                    y: [0, 10, 0],
                    rotate: [0, -10, 0]
                  }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    delay: 0.5
                  }}
                >
                  🌱
                </motion.div>
                <motion.div
                  className="absolute -top-2 -left-6 bg-pink-400 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg border-2 border-white/30"
                  animate={{
                    x: [0, -8, 0],
                    rotate: [0, -5, 0]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: 2
                  }}
                >
                  ✨
                </motion.div>
              </div>
            </motion.div>

            <h1 className={`text-4xl font-bold mb-4 text-center bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text`}>
              {t('v3/personaOnboarding:header.mainTitle')}
            </h1>

            <p className="text-lg text-gray-600 dark:text-gray-300 text-center max-w-md mb-8 leading-relaxed">
              {t('v3/personaOnboarding:header.description')}
            </p>

            {/* 语言切换器 */}
            <div className="mb-6">
              <LanguageQuickSwitcher variant="minimal" className="mx-auto" />
            </div>

            <div className="flex flex-col items-center">
              <motion.button
                onClick={() => scrollToSection(identityRef)}
                className="px-10 py-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium text-lg border border-white/30"
                whileHover={{ scale: 1.05, boxShadow: "0 15px 30px -5px rgba(59, 130, 246, 0.5)" }}
                whileTap={{ scale: 0.95 }}
              >
                {t('v3/personaOnboarding:header.startExplore')}
              </motion.button>

              {/* 添加跳过选项 */}
              {isFirstTimeOnboarding && (
                <button
                  onClick={handleSkip}
                  className="mt-4 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors text-center"
                >
                  {t('v3/personaOnboarding:skip.title', '跳过个性化设置')}
                  <br />
                  <span className="text-xs">{t('v3/personaOnboarding:skip.subtitle', '（您可以稍后在个人选项卡重新配置）')}</span>
                </button>
              )}
            </div>

            <motion.div
              className="mt-16 text-gray-500 dark:text-gray-400 flex flex-col items-center"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <p className="mb-2">{t('v3/personaOnboarding:header.clickToContinue')}</p>
              <div className="flex gap-2 items-center">
                <span className="px-2 py-1 bg-gray-200 dark:bg-gray-800 rounded text-sm">↓</span>
                <span className="px-2 py-1 bg-gray-200 dark:bg-gray-800 rounded text-sm">{t('v3/personaOnboarding:header.keyboardShortcut')}</span>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* 身份选择区块 */}
        <motion.section
          ref={identityRef}
          id="identity"
          className="min-h-[100vh] py-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className={`bg-gradient-to-br ${sectionColors.identity.from.replace('from-', 'from-')}/5 ${sectionColors.identity.to.replace('to-', 'to-')}/5 dark:${sectionColors.identity.from.replace('from-', 'from-')}/10 dark:${sectionColors.identity.to.replace('to-', 'to-')}/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-800/30`}>
            <h2 className={`text-2xl font-bold mb-2 text-center bg-gradient-to-r ${sectionColors.identity.from.replace('from-', 'from-').replace('400', '500')} ${sectionColors.identity.to.replace('to-', 'to-').replace('500', '600')} text-transparent bg-clip-text`}>
              {t('v3/personaOnboarding:sections.identity.title')}
            </h2>

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              {t('v3/personaOnboarding:sections.identity.description')}
            </p>

            {renderTagCloud(
              identityOptions,
              persona.identities,
              (item) => handleMultiSelect('identities', item),
              sectionColors.identity,
              'identities'
            )}

            <div className="flex justify-center mt-10">
              <motion.button
                onClick={() => scrollToSection(habitsRef)}
                className={`px-6 py-2 rounded-full bg-gradient-to-r ${sectionColors.identity.from.replace('from-', 'from-').replace('400', '500')} ${sectionColors.identity.to.replace('to-', 'to-').replace('500', '500')} text-white font-medium border border-white/30`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('v3/personaOnboarding:common.continue')}
              </motion.button>
            </div>
          </div>
        </motion.section>

        {/* 习惯区块 */}
        <motion.section
          ref={habitsRef}
          id="habits"
          className="min-h-[100vh] py-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="bg-gradient-to-br from-pink-400/5 to-red-500/5 dark:from-pink-900/10 dark:to-red-900/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-800/30">
            <h2 className="text-2xl font-bold mb-2 text-center bg-gradient-to-r from-pink-500 to-red-600 text-transparent bg-clip-text">
              {t('v3/personaOnboarding:sections.habits.title')}
            </h2>

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              {t('v3/personaOnboarding:sections.habits.description')}
            </p>

            {renderIconSelector(
              habitOptions,
              persona.habits,
              (item) => handleMultiSelect('habits', item),
              sectionColors.habits,
              'habits'
            )}

            <div className="flex justify-center mt-10">
              <motion.button
                onClick={() => scrollToSection(activitiesRef)}
                className="px-6 py-2 rounded-full bg-gradient-to-r from-pink-500 to-red-500 text-white font-medium border border-white/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('v3/personaOnboarding:common.continue')}
              </motion.button>
            </div>
          </div>
        </motion.section>

        {/* 活动区块 */}
        <motion.section
          ref={activitiesRef}
          id="activities"
          className="min-h-[100vh] py-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="bg-gradient-to-br from-red-400/5 to-orange-500/5 dark:from-red-900/10 dark:to-orange-900/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-800/30">
            <h2 className="text-2xl font-bold mb-2 text-center bg-gradient-to-r from-red-500 to-orange-600 text-transparent bg-clip-text">
              {t('v3/personaOnboarding:sections.activities.title')}
            </h2>

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              {t('v3/personaOnboarding:sections.activities.description')}
            </p>

            {renderTagCloud(
              activityOptions,
              persona.activities,
              (item) => handleMultiSelect('activities', item),
              sectionColors.activities,
              'activities'
            )}

            <div className="flex justify-center mt-10">
              <motion.button
                onClick={() => scrollToSection(challengesRef)}
                className="px-6 py-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white font-medium border border-white/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('v3/personaOnboarding:common.continue')}
              </motion.button>
            </div>
          </div>
        </motion.section>

        {/* 挑战区块 */}
        <motion.section
          ref={challengesRef}
          id="challenges"
          className="min-h-[100vh] py-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="bg-gradient-to-br from-orange-400/5 to-yellow-500/5 dark:from-orange-900/10 dark:to-yellow-900/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-800/30">
            <h2 className="text-2xl font-bold mb-2 text-center bg-gradient-to-r from-orange-500 to-yellow-600 text-transparent bg-clip-text">
              {t('v3/personaOnboarding:sections.challenges.title')}
            </h2>

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              {t('v3/personaOnboarding:sections.challenges.description')}
            </p>

            {renderBubbleSelector(
              challengeOptions,
              persona.challenges,
              (item) => handleMultiSelect('challenges', item),
              sectionColors.challenges,
              'challenges'
            )}

            <div className="flex justify-center mt-10">
              <motion.button
                onClick={() => scrollToSection(goalsRef)}
                className="px-6 py-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-medium border border-white/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('v3/personaOnboarding:common.continue')}
              </motion.button>
            </div>
          </div>
        </motion.section>

        {/* 目标区块 */}
        <motion.section
          ref={goalsRef}
          id="goals"
          className="min-h-[100vh] py-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="bg-gradient-to-br from-yellow-400/5 to-green-500/5 dark:from-yellow-900/10 dark:to-green-900/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-800/30">
            <h2 className="text-2xl font-bold mb-2 text-center bg-gradient-to-r from-yellow-500 to-green-600 text-transparent bg-clip-text">
              {t('v3/personaOnboarding:sections.goals.title')}
            </h2>

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              {t('v3/personaOnboarding:sections.goals.description')}
            </p>

            {renderBubbleSelector(
              goalOptions,
              persona.goals,
              (item) => handleMultiSelect('goals', item),
              sectionColors.goals,
              'goals'
            )}

            <div className="flex justify-center mt-10">
              <motion.button
                onClick={() => scrollToSection(preferencesRef)}
                className="px-6 py-2 rounded-full bg-gradient-to-r from-yellow-500 to-green-500 text-white font-medium border border-white/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('v3/personaOnboarding:common.continue')}
              </motion.button>
            </div>
          </div>
        </motion.section>
        {/* 偏好区块 */}
        <motion.section
          ref={preferencesRef}
          id="preferences"
          className="min-h-[100vh] py-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="bg-gradient-to-br from-green-400/5 to-teal-500/5 dark:from-green-900/10 dark:to-teal-900/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-800/30">
            <h2 className="text-2xl font-bold mb-2 text-center bg-gradient-to-r from-green-500 to-teal-600 text-transparent bg-clip-text">
              {t('v3/personaOnboarding:sections.preferences.title')}
            </h2>

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              {t('v3/personaOnboarding:sections.preferences.description')}
            </p>

            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-green-600 dark:text-green-400">{t('v3/personaOnboarding:sections.preferences.planningStyle')}</h3>
              {renderTagCloud(
                planningStyleOptions,
                persona.preferences.planningStyle,
                (item) => handlePreferenceSelect('planningStyle', item),
                sectionColors.preferences,
                'planningStyle'
              )}
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-green-600 dark:text-green-400">{t('v3/personaOnboarding:sections.preferences.socialStyle')}</h3>
              {renderTagCloud(
                socialStyleOptions,
                persona.preferences.socialStyle,
                (item) => handlePreferenceSelect('socialStyle', item),
                sectionColors.preferences,
                'socialStyle'
              )}
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3 text-green-600 dark:text-green-400">{t('v3/personaOnboarding:sections.preferences.focusStyle')}</h3>
              {renderTagCloud(
                focusStyleOptions,
                persona.preferences.focusStyle,
                (item) => handlePreferenceSelect('focusStyle', item),
                sectionColors.preferences,
                'focusStyle'
              )}
            </div>

            <div className="flex justify-center mt-10">
              <motion.button
                onClick={() => scrollToSection(aiPreferencesRef)}
                className="px-6 py-2 rounded-full bg-gradient-to-r from-green-500 to-teal-500 text-white font-medium border border-white/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('v3/personaOnboarding:common.continue')}
              </motion.button>
            </div>
          </div>
        </motion.section>

        {/* AI助手偏好区块 */}
        <motion.section
          ref={aiPreferencesRef}
          id="aiPreferences"
          className="min-h-[100vh] py-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="bg-gradient-to-br from-teal-400/5 to-blue-500/5 dark:from-teal-900/10 dark:to-blue-900/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-800/30">
            <h2 className="text-2xl font-bold mb-2 text-center bg-gradient-to-r from-teal-500 to-blue-600 text-transparent bg-clip-text">
              {t('v3/personaOnboarding:sections.aiPreferences.title')}
            </h2>

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              {t('v3/personaOnboarding:sections.aiPreferences.description')}
            </p>

            {renderIconSelector(
              aiPreferenceOptions,
              persona.aiPreferences,
              (item) => handleMultiSelect('aiPreferences', item),
              sectionColors.aiPreferences,
              'aiPreferences'
            )}

            <div className="flex justify-center mt-10">
              <motion.button
                onClick={() => scrollToSection(summaryRef)}
                className="px-6 py-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-500 text-white font-medium border border-white/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('v3/personaOnboarding:common.continue')}
              </motion.button>
            </div>
          </div>
        </motion.section>

        {/* 总结区块 */}
        <motion.section
          ref={summaryRef}
          id="summary"
          className="min-h-[100vh] py-4 relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* 背景装饰 */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-400/10 dark:bg-blue-900/20 rounded-full -mr-32 -mt-32 z-0" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-indigo-400/10 dark:bg-indigo-900/20 rounded-full -ml-28 -mb-28 z-0" />

          <div className="bg-gradient-to-br from-blue-400/5 to-indigo-500/5 dark:from-blue-900/10 dark:to-indigo-900/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 dark:border-gray-800/30 relative z-10">
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2">
              <motion.div
                className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center border-2 border-white/30 dark:border-gray-800/30"
                animate={{
                  y: [0, -10, 0],
                  rotate: [0, 10, 0]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity
                }}
              >
                <span className="text-3xl">🎉</span>
              </motion.div>
            </div>

            <h2 className="text-3xl font-bold mb-3 text-center bg-gradient-to-r from-blue-500 to-indigo-600 text-transparent bg-clip-text mt-8">
              {t('v3/personaOnboarding:sections.summary.title')}
            </h2>

            <p className="text-lg text-gray-600 dark:text-gray-300 text-center mb-8 max-w-lg mx-auto">
              {t('v3/personaOnboarding:sections.summary.description')}
            </p>

            <div className="bg-white/80 dark:bg-ios-dark-gray5/80 backdrop-blur-md rounded-xl p-8 border border-white/30 dark:border-gray-700/30 mb-10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-blue-50 dark:bg-blue-900/10 rounded-full -mr-20 -mt-20" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-50 dark:bg-indigo-900/10 rounded-full -ml-16 -mb-16" />

              <div className="relative z-10">
                <h3 className="text-xl font-semibold mb-6 text-blue-600 dark:text-blue-400 flex items-center">
                  <span className="mr-2">{t('v3/personaOnboarding:common.icons.clipboard')}</span> {t('v3/personaOnboarding:sections.summary.personalizedSummary')}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {persona.identities.length > 0 && (
                    <div className="mb-4">
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                        <span className="mr-2 text-blue-500">{t('v3/personaOnboarding:common.icons.person')}</span> {t('v3/personaOnboarding:sections.summary.identity')}:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {persona.identities.map(item => {
                          const tagColor = getTagColor(item, 'identities');
                          return (
                            <span key={item} className={`px-3 py-1.5 ${tagColor.bg} ${tagColor.text} rounded-full text-sm font-medium flex items-center`}>
                              {tagColor.icon && <span className="mr-1">{tagColor.icon}</span>}
                              {item}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {persona.habits.length > 0 && (
                    <div className="mb-4">
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                        <span className="mr-2 text-pink-500">{t('v3/personaOnboarding:common.icons.cycle')}</span> {t('v3/personaOnboarding:sections.summary.habits')}:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {persona.habits.map(item => {
                          const tagColor = getTagColor(item, 'habits');
                          return (
                            <span key={item} className={`px-3 py-1.5 ${tagColor.bg} ${tagColor.text} rounded-full text-sm font-medium flex items-center`}>
                              {tagColor.icon && <span className="mr-1">{tagColor.icon}</span>}
                              {item}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {persona.activities.length > 0 && (
                    <div className="mb-4">
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                        <span className="mr-2 text-orange-500">{t('v3/personaOnboarding:common.icons.target')}</span> {t('v3/personaOnboarding:sections.summary.activities')}:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {persona.activities.map(item => {
                          const tagColor = getTagColor(item, 'activities');
                          const icon = activityIconMap[item] || tagColor.icon;
                          return (
                            <span key={item} className={`px-3 py-1.5 ${tagColor.bg} ${tagColor.text} rounded-full text-sm font-medium flex items-center`}>
                              {icon && <span className="mr-1">{icon}</span>}
                              {item}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {persona.challenges.length > 0 && (
                    <div className="mb-4">
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                        <span className="mr-2 text-yellow-500">{t('v3/personaOnboarding:common.icons.warning')}</span> {t('v3/personaOnboarding:sections.summary.challenges')}:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {persona.challenges.map(item => {
                          const tagColor = getTagColor(item, 'challenges');
                          const icon = challengeIconMap[item] || tagColor.icon;
                          return (
                            <span key={item} className={`px-3 py-1.5 ${tagColor.bg} ${tagColor.text} rounded-full text-sm font-medium flex items-center`}>
                              {icon && <span className="mr-1">{icon}</span>}
                              {item}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {persona.goals.length > 0 && (
                    <div className="mb-4">
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                        <span className="mr-2 text-green-500">{t('v3/personaOnboarding:common.icons.target')}</span> {t('v3/personaOnboarding:sections.summary.goals')}:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {persona.goals.map(item => {
                          const tagColor = getTagColor(item, 'goals');
                          const icon = goalIconMap[item] || tagColor.icon;
                          return (
                            <span key={item} className={`px-3 py-1.5 ${tagColor.bg} ${tagColor.text} rounded-full text-sm font-medium flex items-center`}>
                              {icon && <span className="mr-1">{icon}</span>}
                              {item}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {Object.values(persona.preferences).some(arr => arr.length > 0) && (
                    <div className="mb-4 md:col-span-2">
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                        <span className="mr-2 text-teal-500">{t('v3/personaOnboarding:common.icons.gear')}</span> {t('v3/personaOnboarding:sections.summary.preferences')}:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {persona.preferences.planningStyle.length > 0 && (
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('v3/personaOnboarding:sections.summary.planningStyle')}:</p>
                            <div className="flex flex-wrap gap-2">
                              {persona.preferences.planningStyle.map(item => {
                                const tagColor = getTagColor(item, 'planningStyle');
                                return (
                                  <span key={item} className={`px-2 py-1 ${tagColor.bg} ${tagColor.text} rounded-full text-xs font-medium flex items-center`}>
                                    {tagColor.icon && <span className="mr-1">{tagColor.icon}</span>}
                                    {item}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {persona.preferences.socialStyle.length > 0 && (
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('v3/personaOnboarding:sections.summary.socialStyle')}:</p>
                            <div className="flex flex-wrap gap-2">
                              {persona.preferences.socialStyle.map(item => {
                                const tagColor = getTagColor(item, 'socialStyle');
                                return (
                                  <span key={item} className={`px-2 py-1 ${tagColor.bg} ${tagColor.text} rounded-full text-xs font-medium flex items-center`}>
                                    {tagColor.icon && <span className="mr-1">{tagColor.icon}</span>}
                                    {item}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {persona.preferences.focusStyle.length > 0 && (
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('v3/personaOnboarding:sections.summary.focusStyle')}:</p>
                            <div className="flex flex-wrap gap-2">
                              {persona.preferences.focusStyle.map(item => {
                                const tagColor = getTagColor(item, 'focusStyle');
                                return (
                                  <span key={item} className={`px-2 py-1 ${tagColor.bg} ${tagColor.text} rounded-full text-xs font-medium flex items-center`}>
                                    {tagColor.icon && <span className="mr-1">{tagColor.icon}</span>}
                                    {item}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {persona.aiPreferences.length > 0 && (
                    <div className="mb-4 md:col-span-2">
                      <p className="font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                        <span className="mr-2 text-cyan-500">{t('v3/personaOnboarding:common.icons.robot')}</span> {t('v3/personaOnboarding:sections.summary.aiPreferences')}:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {persona.aiPreferences.map(item => {
                          const tagColor = getTagColor(item, 'aiPreferences');
                          const icon = aiPreferenceIconMap[item] || tagColor.icon;
                          return (
                            <span key={item} className={`px-3 py-1.5 ${tagColor.bg} ${tagColor.text} rounded-full text-sm font-medium flex items-center`}>
                              {icon && <span className="mr-1">{icon}</span>}
                              {item}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <motion.button
                onClick={handleComplete}
                className="px-10 py-4 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium text-lg shadow-lg"
                whileHover={{ scale: 1.05, boxShadow: "0 15px 30px -5px rgba(59, 130, 246, 0.5)" }}
                whileTap={{ scale: 0.95 }}
              >
                {isEditMode ? '保存设置' : t('v3/personaOnboarding:sections.summary.complete')}
              </motion.button>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default PersonaOnboarding;